import React from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Skeleton } from "@/components/ui/skeleton";

export function money(v) {
  const n = Number(v || 0);
  return "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

export function moneyShort(v) {
  const n = Number(v || 0);
  if (n >= 10000000) return "₹" + (n / 10000000).toFixed(2) + "Cr";
  if (n >= 100000) return "₹" + (n / 100000).toFixed(2) + "L";
  if (n >= 1000) return "₹" + (n / 1000).toFixed(1) + "K";
  return "₹" + n;
}

const STATUS_STYLES = {
  // generic
  new: "bg-info/10 text-info border-info/20",
  contacted: "bg-primary/10 text-primary border-primary/20",
  qualified: "bg-automation/10 text-automation border-automation/20",
  unqualified: "bg-muted text-muted-foreground border-border",
  // health / sla
  healthy: "bg-success/10 text-success border-success/20",
  at_risk: "bg-warning/10 text-warning border-warning/20",
  breached: "bg-critical/10 text-critical border-critical/20",
  // invoices
  paid: "bg-success/10 text-success border-success/20",
  unpaid: "bg-muted text-muted-foreground border-border",
  partially_paid: "bg-warning/10 text-warning border-warning/20",
  overdue: "bg-critical/10 text-critical border-critical/20",
  payment_pending: "bg-info/10 text-info border-info/20",
  // tickets / tasks
  open: "bg-info/10 text-info border-info/20",
  in_progress: "bg-primary/10 text-primary border-primary/20",
  waiting: "bg-warning/10 text-warning border-warning/20",
  blocked: "bg-critical/10 text-critical border-critical/20",
  resolved: "bg-success/10 text-success border-success/20",
  completed: "bg-success/10 text-success border-success/20",
  pending: "bg-muted text-muted-foreground border-border",
  // quotes
  draft: "bg-muted text-muted-foreground border-border",
  sent: "bg-info/10 text-info border-info/20",
  accepted: "bg-success/10 text-success border-success/20",
  rejected: "bg-critical/10 text-critical border-critical/20",
  // priority
  low: "bg-muted text-muted-foreground border-border",
  medium: "bg-info/10 text-info border-info/20",
  high: "bg-escalation/10 text-escalation border-escalation/20",
  urgent: "bg-critical/10 text-critical border-critical/20",
  critical: "bg-critical/10 text-critical border-critical/20",
};

export function StatusBadge({ value, className }) {
  const key = String(value || "").toLowerCase();
  const label = String(value || "—").replace(/_/g, " ");
  return (
    <span
      data-testid={`status-badge-${key}`}
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize",
        STATUS_STYLES[key] || "bg-muted text-muted-foreground border-border",
        className
      )}
    >
      {label}
    </span>
  );
}

export function StatCard({ icon: Icon, label, value, sub, tone = "primary", index = 0, testid }) {
  const tones = {
    primary: "text-primary bg-primary/10",
    success: "text-success bg-success/10",
    warning: "text-warning bg-warning/10",
    critical: "text-critical bg-critical/10",
    info: "text-info bg-info/10",
    automation: "text-automation bg-automation/10",
  };
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: index * 0.05 }}
      className="rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="mt-2 font-heading text-2xl font-bold text-foreground">{value}</p>
          {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
        </div>
        {Icon && (
          <div className={cn("flex h-10 w-10 items-center justify-center rounded-lg", tones[tone])}>
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div data-testid={testid} className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card/50 px-6 py-16 text-center">
      {Icon && (
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-7 w-7" />
        </div>
      )}
      <h3 className="font-heading text-lg font-semibold text-foreground">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Loading({ rows = 5 }) {
  return (
    <div className="space-y-3" data-testid="loading-state">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-14 w-full rounded-lg" />
      ))}
    </div>
  );
}

function renderInline(text, keyBase) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={`${keyBase}-${i}`}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("`") && p.endsWith("`")) return <code key={`${keyBase}-${i}`} className="rounded bg-muted px-1 py-0.5 text-[0.85em]">{p.slice(1, -1)}</code>;
    return <React.Fragment key={`${keyBase}-${i}`}>{p}</React.Fragment>;
  });
}

export function MarkdownLite({ text }) {
  const lines = String(text || "").split("\n");
  const blocks = [];
  let list = null;

  const flush = () => {
    if (list) { blocks.push({ type: "list", ordered: list.ordered, items: list.items }); list = null; }
  };

  lines.forEach((raw) => {
    const line = raw.replace(/\s+$/, "");
    if (/^\s*[-*]\s+/.test(line)) {
      if (!list || list.ordered) { flush(); list = { ordered: false, items: [] }; }
      list.items.push(line.replace(/^\s*[-*]\s+/, ""));
    } else if (/^\s*\d+\.\s+/.test(line)) {
      if (!list || !list.ordered) { flush(); list = { ordered: true, items: [] }; }
      list.items.push(line.replace(/^\s*\d+\.\s+/, ""));
    } else if (/^#{1,3}\s+/.test(line)) {
      flush(); blocks.push({ type: "h", text: line.replace(/^#{1,3}\s+/, "") });
    } else if (/^\s*---+\s*$/.test(line)) {
      flush(); blocks.push({ type: "hr" });
    } else if (line.trim() === "") {
      flush(); blocks.push({ type: "space" });
    } else {
      flush(); blocks.push({ type: "p", text: line });
    }
  });
  flush();

  return (
    <div className="space-y-1.5 text-sm leading-relaxed">
      {blocks.map((b, i) => {
        if (b.type === "h") return <p key={i} className="mt-1 font-heading text-sm font-semibold">{renderInline(b.text, i)}</p>;
        if (b.type === "hr") return <hr key={i} className="my-2 border-border" />;
        if (b.type === "space") return <div key={i} className="h-1" />;
        if (b.type === "list") return b.ordered ? (
          <ol key={i} className="ml-4 list-decimal space-y-1">{b.items.map((it, j) => <li key={j}>{renderInline(it, `${i}-${j}`)}</li>)}</ol>
        ) : (
          <ul key={i} className="ml-4 list-disc space-y-1">{b.items.map((it, j) => <li key={j}>{renderInline(it, `${i}-${j}`)}</li>)}</ul>
        );
        return <p key={i}>{renderInline(b.text, i)}</p>;
      })}
    </div>
  );
}
